"use client";
import { useRouter } from "next/navigation";
import { useEffect, useState, useRef, JSX } from "react";
import { v4 as uuidv4 } from "uuid";
import Image from "next/image";
import { cx } from "lib/cx";
import { useAppDispatch } from "lib/redux/hooks";
import { setTemplate } from "lib/redux/settingsSlice";
import { changeTemplateId } from "lib/redux/resumeSlice";
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCoverflow } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';


interface Template {
  id: number;
  imageUrl: string;
  name: string;
  columns: "single" | "double";
}

interface MobileCarouselProps {
  templateList: Template[];
}

export default function MobileTemplatesPreviewCarousel({
  templateList,
}: MobileCarouselProps) {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const [goToSlide, setGoToSlide] = useState(0);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [refsReady, setRefsReady] = useState(false);
  const swiperRef = useRef<SwiperType>();

  // Function to check if refs are fully populated
  const handleRefsReady = () => {
    if (slideRefs.current.length === templateList.length) {
      setRefsReady(true);
    }
  };

  // Update slide border using useEffect
  useEffect(() => {
    if (refsReady) {
      // Clear border for all slides
      slideRefs.current.forEach((slide, index) => {
        if (slide) {
          slide.classList.remove("custom-border-blue");
          slide.classList.add("border-[#EEEEEE]");
        }
      });

      // Add border to the active slide
      if (slideRefs.current[goToSlide]) {
        slideRefs.current[goToSlide]?.classList.add("custom-border-blue");
      }
    }
  }, [goToSlide, refsReady]);

  // Handle navigation between slides
  const handleNextSlide = () => {
    const nextIndex = (goToSlide + 1) % templateList.length;
    setGoToSlide(nextIndex);
    swiperRef.current?.slideTo(nextIndex);
  };

  const handlePreviousSlide = () => {
    const prevIndex = goToSlide === 0 ? templateList.length - 1 : goToSlide - 1;
    setGoToSlide(prevIndex);
    swiperRef.current?.slideTo(prevIndex);
  };

  // Handle slide change
  const handleSlideChange = (swiper: SwiperType) => {
    setGoToSlide(swiper.activeIndex);
  };

  // Handle slide click
  const handleSlideClick = (index: number) => {
    setGoToSlide(index);
    swiperRef.current?.slideTo(index);
  };

  useEffect(() => {
    dispatch(setTemplate(templateList[goToSlide].id));
    dispatch(changeTemplateId(templateList[goToSlide].id));
  }, [goToSlide, dispatch, templateList]);

  return (
    <div className="relative flex flex-col items-center gap-4">
      <div className="h-[250px] w-full">
        <Swiper
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          effect={'coverflow'}
          grabCursor={true}
          centeredSlides={true}
          slidesPerView={'auto'}
          coverflowEffect={{
            rotate: 0,
            stretch: 0,
            depth: 100,
            modifier: 2,
            slideShadows: false,
          }}
          modules={[EffectCoverflow]}
          onSlideChange={handleSlideChange}
          initialSlide={goToSlide}
          className="mobile-carousel-swiper h-full w-full"
          style={{
            overflow: 'visible',
          }}
        >
          {templateList.map((template, index) => (
            <SwiperSlide
              key={uuidv4()}
              style={{ 
                width: 'auto',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
              onClick={() => handleSlideClick(index)}
            >
              <div
                ref={(el) => {
                  slideRefs.current[index] = el;
                  handleRefsReady();
                }}
                className={cx(
                  "relative overflow-hidden border-[4px] bg-[#EEEEEE] cursor-pointer transition-opacity duration-300",
                  "h-[246px] w-[190px] md:h-[491px] md:w-[400px]",
                  "border-[#EEEEEE]"
                )}
                style={{ flexShrink: 0 }}
              >
                <div className="overflow-hidden border-[#EEEEEE]">
                  <Image
                    src={template.imageUrl}
                    alt={template.name}
                    fill={true}
                    loading={goToSlide === index ? "eager" : "lazy"}
                    priority={goToSlide === index}
                    style={{ objectFit: "contain" }}
                  />
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
}
